import { useAuthContext } from '@/auth/useAuthContext';
import { setActiveFacility } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Building, ChevronDown } from 'lucide-react';
import { useState } from 'react';

export function FacilityPicker({ compact = false }: { compact?: boolean }) {
  const { state, refresh } = useAuthContext();
  const [open, setOpen] = useState(false);

  const inScope = state.me?.facilitiesInScope ?? [];
  // Only one facility (or none) — no choice to make, render nothing.
  if (inScope.length <= 1) return null;

  const active = state.me?.activeFacility;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Switch facility"
        className={cn(
          'flex items-center gap-1.5 rounded-md text-muted-foreground hover:text-foreground press',
          compact ? 'px-1.5 py-1' : 'border border-border bg-surface px-3 py-1.5 text-sm',
        )}
      >
        <Building className={compact ? 'size-4' : 'size-3.5'} strokeWidth={1.75} />
        {!compact && <span className="font-medium">{active?.name ?? 'Select facility'}</span>}
        {!compact && <ChevronDown className="size-3.5" strokeWidth={1.75} />}
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-40 rounded-lg border border-border bg-surface shadow-lg ring-1 ring-border',
            compact ? 'left-12 top-0 w-56' : 'left-0 top-full mt-1 w-56',
          )}
        >
          <button
            type="button"
            onClick={async () => {
              await setActiveFacility(null);
              await refresh();
              setOpen(false);
            }}
            className={cn(
              'flex w-full items-center px-3 py-2 text-sm hover:bg-secondary press',
              active == null && 'bg-secondary font-medium',
            )}
          >
            All facilities
          </button>
          {inScope.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={async () => {
                await setActiveFacility(f.id);
                await refresh();
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center px-3 py-2 text-sm hover:bg-secondary press',
                active?.id === f.id && 'bg-secondary font-medium',
              )}
            >
              {f.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
